import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { CustomerDetails as ICustomerDetails } from './OrderData';
import { deliveryZoneService } from '../../services/delivery-zones';

interface CustomerDetailsProps {
  onNext: () => void;
  onBack: () => void;
  onSubmit: (data: ICustomerDetails) => Promise<void>;
  onCancel?: () => void;
  cancelling?: boolean;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  onNext,
  onBack,
  onSubmit,
  onCancel,
  cancelling,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveryValidation, setDeliveryValidation] = useState<{
    isValid: boolean;
    message: string;
    zone?: any;
  } | null>(null);
  const [validatingAddress, setValidatingAddress] = useState(false);

  const formik = useFormik<ICustomerDetails>({
    initialValues: {
      is_first_time: true,
      adults_count: 1,
      children_count: 0,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      delivery_method: 'pickup',
      address: '',
      suburb: '',
      postcode: '',
      notes: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      phone: Yup.string().required('Phone number is required'),
      delivery_method: Yup.string().oneOf(['delivery', 'pickup']).required('Delivery method is required'),
      address: Yup.string().when('delivery_method', {
        is: 'delivery',
        then: (schema) => schema.required('Address is required for delivery'),
        otherwise: (schema) => schema.notRequired(),
      }),
      suburb: Yup.string().when('delivery_method', {
        is: 'delivery',
        then: (schema) => schema.required('Suburb is required for delivery'),
        otherwise: (schema) => schema.notRequired(),
      }),
      postcode: Yup.string().when('delivery_method', {
        is: 'delivery',
        then: (schema) => schema.required('Postcode is required for delivery'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values: ICustomerDetails, { setSubmitting }: FormikHelpers<ICustomerDetails>) => {
      setLoading(true);
      setError(null);
      try {
        const customerData: ICustomerDetails = {
          ...values,
          is_first_time: Boolean(values.is_first_time),
        };
        await onSubmit(customerData);
        onNext();
      } catch (err: any) {
        setError(err.response?.data?.message || 'An unexpected error occurred.');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Validate delivery address when postcode or suburb changes (only for delivery)
  useEffect(() => {
    if (formik.values.delivery_method === 'delivery' && formik.values.postcode && formik.values.suburb) {
      const timeoutId = setTimeout(() => {
        validateDeliveryAddress(formik.values.postcode, formik.values.suburb);
      }, 500); // Debounce validation

      return () => clearTimeout(timeoutId);
    } else {
      setDeliveryValidation(null);
    }
  }, [formik.values.postcode, formik.values.suburb, formik.values.delivery_method]);

  const validateDeliveryAddress = async (postcode: string, suburb: string) => {
    if (!postcode || !suburb) {
      setDeliveryValidation(null);
      return;
    }

    setValidatingAddress(true);
    
    try {
      const validation = await deliveryZoneService.validateDeliveryAddress(postcode, suburb);
      
      setDeliveryValidation({
        isValid: validation.is_valid,
        message: validation.message,
        zone: validation.zone
      });
      
    } catch (error) {
      console.warn('Error validating delivery address:', error);
      setDeliveryValidation({
        isValid: false,
        message: 'Unable to validate delivery address. Please try again or contact us for assistance.'
      });
    } finally {
      setValidatingAddress(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Customer Details
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      
      {/* Delivery Availability Notice */}
      {formik.values.delivery_method === 'delivery' && deliveryValidation && !deliveryValidation.isValid && !validatingAddress && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Delivery Not Available
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            We don't deliver to the address you've provided, but you can still place your order and pick it up from our location.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            You can switch to pickup by selecting "Pickup from our location" above.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Is this the first time you are ordering with us?</FormLabel>
                <RadioGroup
                  row
                  name="is_first_time"
                  value={String(formik.values.is_first_time)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => formik.setFieldValue('is_first_time', e.target.value === 'true')}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="adults_count"
                name="adults_count"
                label="Number of Adults"
                type="number"
                value={formik.values.adults_count}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="children_count"
                name="children_count"
                label="Number of Children"
                type="number"
                value={formik.values.children_count}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="firstName"
                name="firstName"
                label="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="email"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="phone"
                name="phone"
                label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Delivery Method
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">How would you like to receive your order? (You can change it during checkout)</FormLabel>
                <RadioGroup
                  row
                  name="delivery_method"
                  value={formik.values.delivery_method}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.setFieldValue('delivery_method', e.target.value);
                    // Clear address fields when switching to pickup
                    if (e.target.value === 'pickup') {
                      formik.setFieldValue('address', '');
                      formik.setFieldValue('suburb', '');
                      formik.setFieldValue('postcode', '');
                      setDeliveryValidation(null);
                    }
                  }}
                >
                  <FormControlLabel 
                    value="delivery" 
                    control={<Radio />} 
                    label="Delivery to my address" 
                  />
                  <FormControlLabel 
                    value="pickup" 
                    control={<Radio />} 
                    label="Pickup from our location" 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          {formik.values.delivery_method === 'delivery' && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Delivery Address
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="address"
                    name="address"
                    label="Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    id="suburb"
                    name="suburb"
                    label="Suburb"
                    value={formik.values.suburb}
                    onChange={formik.handleChange}
                    error={formik.touched.suburb && Boolean(formik.errors.suburb)}
                    helperText={formik.touched.suburb && formik.errors.suburb}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    id="postcode"
                    name="postcode"
                    label="Postcode"
                    value={formik.values.postcode}
                    onChange={formik.handleChange}
                    error={formik.touched.postcode && Boolean(formik.errors.postcode)}
                    helperText={formik.touched.postcode && formik.errors.postcode}
                  />
                </Grid>
            
                {/* Delivery Address Validation */}
                <Grid item xs={12}>
                  {validatingAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Checking delivery availability...
                      </Typography>
                    </Box>
                  )}
                  
                  {deliveryValidation && !validatingAddress && (
                    <Alert 
                      severity={deliveryValidation.isValid ? 'success' : 'warning'}
                      sx={{ mt: 1 }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {deliveryValidation.message}
                      </Typography>
                      {!deliveryValidation.isValid && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          You can still select pickup as your delivery method on the checkout page.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Delivery Notes"
                    multiline
                    rows={4}
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Notes field for pickup orders */}
          {formik.values.delivery_method === 'pickup' && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="notes"
                  name="notes"
                  label="Order Notes"
                  multiline
                  rows={4}
                  value={formik.values.notes}
                  onChange={formik.handleChange}

                />
              </Grid>
            </Grid>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} flexWrap="wrap" gap={1}>
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
            <Box display="flex" gap={1} alignItems="center">
              {onCancel && (
                <Button variant="outlined" color="error" onClick={onCancel} disabled={cancelling}>
                  {cancelling ? 'Cancelling...' : 'Cancel Order'}
                </Button>
              )}
              <Button type="submit" variant="contained" color="primary" disabled={loading || formik.isSubmitting}>
                {loading ? <CircularProgress size={24} /> : 'Next'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CustomerDetails; 